"use client";

import { useEffect, useState } from "react";

const POOLS = [
	["Gly", "Ala", "His", "Lys", "Glu", "Asp"],
	["Pro", "Tyr", "Ser", "Cys", "Phe", "Leu"],
	["Arg", "Met", "Thr", "Trp", "Val", "Ile"],
	["Asn", "Gln", "Aib", "Hyp", "Sar", "Orn"],
	["His", "Lys", "Glu", "Asp", "Pro", "Tyr"],
];

interface CyclingAminoStripProps {
	intervalMs?: number;
}

export function CyclingAminoStrip({ intervalMs = 1800 }: CyclingAminoStripProps) {
	const [idx, setIdx] = useState(0);

	useEffect(() => {
		const reduced =
			typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
		if (reduced) return;
		const id = setInterval(() => setIdx((i) => (i + 1) % POOLS.length), intervalMs);
		return () => clearInterval(id);
	}, [intervalMs]);

	const codes = POOLS[idx];
	const W = 480;
	const H = 56;
	const MID = H / 2;
	const PAD = 32;
	const STEP = (W - 2 * PAD) / (codes.length - 1);

	return (
		<div
			className="inline-flex w-full max-w-[480px] items-center text-primary"
			title="Live peptide sequence"
			aria-hidden
		>
			<svg
				viewBox={`0 0 ${W} ${H}`}
				className="block h-14 w-full"
				preserveAspectRatio="xMidYMid meet"
				style={{ overflow: "visible" }}
			>
				<line x1="0" y1={MID} x2={W} y2={MID} stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.7" />
				<text
					x="2"
					y={MID + 2.5}
					fontSize="8"
					fontFamily="ui-monospace, monospace"
					fill="currentColor"
					fillOpacity="0.9"
				>
					H₂N
				</text>
				<text
					x={W - 2}
					y={MID + 2.5}
					textAnchor="end"
					fontSize="8"
					fontFamily="ui-monospace, monospace"
					fill="currentColor"
					fillOpacity="0.9"
				>
					COOH
				</text>
				{codes.map((c, i) => {
					const x = PAD + i * STEP;
					const isUp = i % 2 === 0;
					return (
						<g key={`${idx}-${i}`} className="transition-opacity duration-500">
							<line
								x1={x}
								y1={MID}
								x2={x}
								y2={isUp ? MID - 10 : MID + 10}
								stroke="currentColor"
								strokeWidth="1"
								strokeOpacity="0.7"
							/>
							<circle
								cx={x}
								cy={isUp ? MID - 12 : MID + 12}
								r="2.3"
								fill="none"
								stroke="currentColor"
								strokeWidth="1"
							/>
							<circle cx={x} cy={MID} r="2.4" fill="currentColor" />
							<text
								x={x}
								y={isUp ? MID - 18 : MID + 22}
								textAnchor="middle"
								fontSize="8"
								fontWeight="500"
								fontFamily="ui-monospace, monospace"
								letterSpacing="0.5"
								fill="currentColor"
								fillOpacity="0.95"
							>
								{c}
							</text>
						</g>
					);
				})}
			</svg>
		</div>
	);
}
